import { useCallback } from 'react';
import { TextSelection } from '@tiptap/pm/state';
import { useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';

const useContentItemActions = (editor, currentNode, currentNodePos) => {
  const { segmentType, tripId } = (currentNode && currentNode.attrs) || {};
  useSelector((state) => state.brochure);
  const { track } = useAnalytics();
  const resetTextFormatting = useCallback(() => {
    const chain = editor.chain();

    chain.setNodeSelection(currentNodePos).unsetAllMarks();

    if (currentNode?.type.name !== 'paragraph') {
      chain.setParagraph();
    }
    chain.run();
    track('brochureEditor.resetTextFormatting', {
      segmentType,
      tripId,
    });
  }, [editor, currentNodePos, currentNode, track, segmentType, tripId]);

  const copyNodeToClipboard = useCallback(() => {
    editor.chain().setMeta('hideDragHandle', true).setNodeSelection(currentNodePos).run();

    document.execCommand('copy');
    track('brochureEditor.copyNodeToClipboard', {
      segmentType,
      tripId,
    });
  }, [editor, currentNodePos, track, segmentType, tripId]);
  const deleteNode = useCallback(() => {
    editor
      .chain()
      .setMeta('hideDragHandle', true)
      .setNodeSelection(currentNodePos)
      .deleteSelection()
      .run();
    track('brochureEditor.deleteNode', {
      segmentType,
      tripId,
    });
  }, [editor, currentNodePos, track, segmentType, tripId]);
  const handleAdd = useCallback(() => {
    if (!editor || currentNodePos === -1 || !currentNode) return;
    const { schema } = editor.state;

    const currentNodeIsEmptyParagraph =
      currentNode.type.name === 'paragraph' && currentNode.content.size === 0;

    const currentNodeSize = currentNode.nodeSize || 0;
    const insertPos = currentNodePos + currentNodeSize;
    const fallbackFocusPos = currentNodeIsEmptyParagraph ? currentNodePos + 2 : insertPos + 2;
    track('brochureEditor.handleAddCommand', {
      segmentType,
      tripId,
    });
    if (currentNode.type.name === 'segmentContainer') {
      const slashParagraph = schema.nodes.paragraph.create(null, [schema.text('/')]);
      const updatedContent = [...currentNode.content.content, slashParagraph];

      const updatedNode = currentNode.type.create(
        currentNode.attrs,
        updatedContent,
        currentNode.marks,
      );

      // Build replacement transaction
      const tr = editor.state.tr.replaceWith(
        currentNodePos,
        currentNodePos + currentNode.nodeSize,
        updatedNode,
      );

      // Recompute new position of slashParagraph (last child in container)
      let newParaPos = currentNodePos + 1;
      for (let i = 0; i < updatedContent.length - 1; i += 1) {
        newParaPos += updatedContent[i].nodeSize;
      }

      // Set selection after '/'
      tr.setSelection(TextSelection.create(tr.doc, newParaPos + 2));

      editor.view.dispatch(tr);
      editor.view.focus();

      // Trigger input manually
      requestAnimationFrame(() => {
        const inputEvent = new InputEvent('input', { bubbles: true });
        editor.view.dom.dispatchEvent(inputEvent);
      });

      return;
    }

    // Fallback for non-segmentContainer
    editor
      .chain()
      .command(({ dispatch, tr, state }) => {
        if (dispatch) {
          if (currentNodeIsEmptyParagraph) {
            tr.insertText('/', currentNodePos, currentNodePos + 1);
          } else {
            tr.insert(
              insertPos,
              state.schema.nodes.paragraph.create(null, [state.schema.text('/')]),
            );
          }

          return dispatch(tr);
        }
        return true;
      })
      .focus(fallbackFocusPos)
      .run();

    requestAnimationFrame(() => {
      const inputEvent = new InputEvent('input', { bubbles: true });
      editor.view.dom.dispatchEvent(inputEvent);
    });
  }, [editor, currentNodePos, currentNode, track, segmentType, tripId]);
  return {
    resetTextFormatting,
    copyNodeToClipboard,
    deleteNode,
    handleAdd,
  };
};

export default useContentItemActions;
